import Dynamic from '@base/Dynamic/Dynamic';
import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';
/* REFERRAL_PROMOTIONS */
import CampaignAuthForm from '@cont/ReferralPromotions/CampaignAuthForm';
/* REFERRAL_PROMOTIONS */
import { useLocalLoginInfo } from './Context/LoginLocalInfo';
import useLoginStepFlags from './utils/hooks/useLoginStepFlags';

const EnterNumberStep = Dynamic(() => import('./EnterNumberStep'));
const EnterEmailStep = Dynamic(() => import('./EnterEmailStep'));
const EnterEmailOtpStep = Dynamic(() => import('./EnterEmailOtpStep'));
const EnterOtpStep = Dynamic(() => import('./EnterOtpStep'));
const EnterPasswordStep = Dynamic(() => import('./EnterPasswordStep'));
const ConfirmAgeStep = Dynamic(() => import('./ConfirmAgeStep'));
const EnterNumberOtpStep = Dynamic(() => import('./EnterNumberOtpStep'));
const NewEnterEmailOTPStep = Dynamic(() => import('./NewEnterEmailOTPStep'));
const UpdateDetailsStep = Dynamic(() => import('./UpdateDetailsStep'));
const RegisterWithEmailOutsideIndia = Dynamic(() =>
  import('./RegisterWithEmailOutsideIndia')
);

const Container = styled.div`
  background: ${({ $primeTheme }) => ($primeTheme ? 'transparent' : 'white')};
  min-width: 480px;
  padding: 40px;
  border-radius: 16px;
  border: ${({ $primeTheme }) =>
    $primeTheme ? 'none' : '1px solid var(--color-divider)'};

  ${({ $primeTheme }) =>
    $primeTheme
      ? `
    --color-base-1: #252038;
    --color-divider: rgba(255, 255, 255, 0.72);
    --color-divider-tertiary: rgba(255, 255, 255, 0.72);
    --color-i-green: #B9A5FF;
    --color-i-green-focus: #9875FF;
    --color-i-white: #9875FF;
    --color-shadow: rgba(255, 255, 255, 0.24);
    --color-text-primary: #ffffff;
    --color-text-primary-light: rgba(255, 255, 255, 0.72);
    --color-text-secondary: rgba(255, 255, 255, 0.72);

    button {
      background-color: #ffffff;
      color: #9875FF;
    }

    input {
      color: #ffffff;
    }
  `
      : ''}

  @media (max-width: 480px) {
    min-width: 320px;
    max-width: 480px;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid var(--color-divider);
  }
`;

const PrimeThemeScope = styled.div`
  ${({ $primeTheme }) =>
    $primeTheme
      ? `
    --color-base-0: #120D23;
    --color-base-1: #252038;
    --color-divider: rgba(255, 255, 255, 0.72);
    --color-divider-tertiary: rgba(255, 255, 255, 0.72);
    --color-i-green: #B9A5FF;
    --color-i-green-focus: #9875FF;
    --color-i-white: #9875FF;
    --color-shadow: rgba(255, 255, 255, 0.24);
    --color-text-primary: #ffffff;
    --color-text-primary-light: rgba(255, 255, 255, 0.72);
    --color-text-secondary: rgba(255, 255, 255, 0.72);
    color: #ffffff;

    input {
      color: #ffffff;
    }
  `
      : ''}
`;

/* REFERRAL_PROMOTIONS — begin */
const CampaignThemeScope = styled.div`
  width: 100%;
  color: #ffffff;
  --color-base-0: transparent;
  --color-base-1: transparent;
  --color-divider: rgba(255, 255, 255, 0.4);
  --color-divider-tertiary: rgba(255, 255, 255, 0.4);
  --color-i-green: #ffffff;
  --color-i-green-focus: #ffffff;
  --color-i-white: #ffffff;
  --color-text-primary: #ffffff;
  --color-text-primary-light: rgba(255, 255, 255, 0.72);
  --color-text-secondary: #a2a0a2;

  input {
    color: #ffffff;
  }

  & .MuiOutlinedInput-root {
    color: #ffffff;
    background: rgba(9, 7, 19, 0.32) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 8px;
    box-shadow: none !important;
  }

  & .MuiOutlinedInput-root.Mui-disabled {
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(255, 255, 255, 0.2) !important;
    opacity: 1 !important;
  }

  & .MuiOutlinedInput-input,
  & .MuiInputBase-input {
    color: #ffffff !important;
    -webkit-text-fill-color: #ffffff !important;
  }

  & .MuiOutlinedInput-input::placeholder {
    color: rgba(255, 255, 255, 0.5);
    opacity: 1;
  }

  & .MuiFormHelperText-root {
    color: rgba(255, 180, 180, 0.95);
  }

  & .MuiInputAdornment-root,
  & .MuiInputAdornment-root * {
    color: #ffffff !important;
  }

  & a,
  & a * {
    color: #ffffff;
  }

  & .campaign-login-cta-row {
    margin-top: 24px;
    align-items: flex-end;
  }

  & .campaign-login-cta-row > button {
    width: 176px !important;
    min-width: 176px;
    height: 48px !important;
    min-height: 48px !important;
    padding: 0 24px;
    border-radius: 8px !important;
    background-color: #ffffff !important;
    color: #3c4852 !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    box-shadow: none !important;
    transform: none !important;
  }

  & .campaign-login-cta-row > button:hover,
  & .campaign-login-cta-row > button:active {
    background-color: #ffffff !important;
    color: #3c4852 !important;
    box-shadow: none !important;
    transform: none !important;
  }

  & .campaign-login-cta-row > button[disabled] {
    background-color: rgba(255, 255, 255, 0.35) !important;
    color: rgba(60, 72, 82, 0.55) !important;
    cursor: not-allowed;
  }

  ${mediaQuery.uptoMobile} {
    h2 {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
    }

    p {
      font-size: 12px;
      line-height: 18px;
      color: rgba(255, 255, 255, 0.72);
    }

    & .campaign-login-cta-row > button {
      width: 128px !important;
      min-width: 128px;
      border-radius: 4px !important;
    }
  }
`;

const LoginUI = () => {
  const {
    showEmailStep,
    showOutsideIndiaRegistrationProcess,
    showFirstStep,
    showAgeStep,
    showPhoneOTPStep,
    showEmailOTPStep,
    showEnterPasswordStep,
    showUpdateDetailsStep
  } = useLoginStepFlags();
  const { IsNewLoginModal, country, primeTheme, campaignTheme } =
    useLocalLoginInfo();
  const isOutsideIndia = country ? country?.code !== 'IN' : false;
  const { countries, isTTU } = useLocalLoginInfo();

  const renderFirstStepForModal = () => {
    if (showEmailStep) return <NewEnterEmailOTPStep />;
    return <EnterNumberOtpStep />;
  };

  if (campaignTheme) {
    return (
      <CampaignThemeScope>
        {showFirstStep && <CampaignAuthForm />}
        {showAgeStep && <ConfirmAgeStep />}
        {showPhoneOTPStep && <EnterOtpStep />}
        {showEmailOTPStep && <EnterEmailOtpStep />}
        {showEnterPasswordStep && <EnterPasswordStep />}
        {showUpdateDetailsStep && <UpdateDetailsStep />}
      </CampaignThemeScope>
    );
  }
  /* REFERRAL_PROMOTIONS — end */

  if (!isOutsideIndia && IsNewLoginModal && !isTTU) {
    return (
      <PrimeThemeScope $primeTheme={primeTheme}>
        {(showFirstStep ||
          ((showPhoneOTPStep || showEmailOTPStep) && !showUpdateDetailsStep)) &&
          renderFirstStepForModal()}
        {showUpdateDetailsStep && <UpdateDetailsStep />}
      </PrimeThemeScope>
    );
  }

  const renderFirstStep = () => {
    if (showEmailStep) return <EnterEmailStep />;
    return <EnterNumberStep />;
  };

  if (!countries.length) return null;
  if (showOutsideIndiaRegistrationProcess)
    return <RegisterWithEmailOutsideIndia />;
  return (
    <Container $primeTheme={primeTheme}>
      {showFirstStep && renderFirstStep()}
      {showAgeStep && <ConfirmAgeStep />}
      {showPhoneOTPStep && <EnterOtpStep />}
      {showEmailOTPStep && <EnterEmailOtpStep />}
      {showEnterPasswordStep && <EnterPasswordStep />}
    </Container>
  );
};

export default LoginUI;
